import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FormControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, startWith, debounceTime, switchMap, distinctUntilChanged } from 'rxjs/operators';

export type SearchType = 'station' | 'truck-trailer' | 'customer';

@Component({
  selector: 'app-search-modal',
  templateUrl: './search-modal.component.html',
  styleUrls: ['./search-modal.component.scss'],
  standalone: false
})
export class SearchModalComponent implements OnInit {
  @Input() searchType!: SearchType;
  @Input() fieldTitle!: string;
  @Input() data: any[] = [];
  @Input() searchFunction?: (term: string) => Observable<any[]>; // For live API search

  searchControl = new FormControl('');
  filteredData: Observable<any[]> = of([]);

  constructor(private modalController: ModalController) {}

  ngOnInit() {
    // If searchFunction is provided, use live API search
    if (this.searchFunction) {
      this.filteredData = this.searchControl.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => {
          const searchTerm = value || '';
          if (searchTerm.length >= 2) {
            return this.searchFunction!(searchTerm);
          }
          return of([]);
        })
      );
    } else {
      // Use local filtering
      this.filteredData = this.searchControl.valueChanges.pipe(
        startWith(''),
        map(value => this.filterData(value || ''))
      );
    }
  }

  private filterData(searchText: string): any[] {
    if (!searchText.trim()) {
      return this.data.slice(0, 50);
    }

    const filterValue = searchText.toLowerCase();
    
    return this.data
      .filter(item => {
        switch (this.searchType) {
          case 'station':
            return (
              (item.Name && item.Name.toLowerCase().includes(filterValue)) ||
              (item.Description && item.Description.toLowerCase().includes(filterValue)) ||
              (item.TankID && item.TankID.toString().includes(filterValue))
            );
          
          case 'truck-trailer':
            return (
              (item.Description && item.Description.toLowerCase().includes(filterValue)) ||
              (item.RegNr && item.RegNr.toLowerCase().includes(filterValue)) ||
              (item.Type && item.Type.toLowerCase().includes(filterValue))
            );
          
          case 'customer':
            const name = (item.Name || item.TranslatedName || item.Title || '').toLowerCase();
            const id = (item.Id || item.CustomerNumber || '').toString();
            return name.includes(filterValue) || id.includes(filterValue);
          
          default:
            return false;
        }
      })
      .slice(0, 50);
  }

  selectItem(item: any) {
    let value = '';
    
    switch (this.searchType) {
      case 'station':
        value = item.Name;
        break;
      case 'truck-trailer':
        value = item.Description;
        break;
      case 'customer':
        value = item.Name || item.TranslatedName || item.Title;
        break;
    }
    
    this.modalController.dismiss(value);
  }

  dismiss() {
    this.modalController.dismiss();
  }

  getDisplayName(item: any): string {
    switch (this.searchType) {
      case 'station':
        return item.Name || '';
      case 'truck-trailer':
        return item.Description || '';
      case 'customer':
        return item.Name || item.TranslatedName || item.Title || '';
      default:
        return '';
    }
  }

  getSecondaryInfo(item: any): string {
    switch (this.searchType) {
      case 'station':
        return item.Description || '';
      case 'truck-trailer':
        return item.RegNr || '';
      case 'customer':
        return item.Id ? `ID: ${item.Id}` : '';
      default:
        return '';
    }
  }

  getTertiaryInfo(item: any): string {
    switch (this.searchType) {
      case 'station':
        return item.TankID ? `ID: ${item.TankID}` : '';
      case 'truck-trailer':
        return item.Type || '';
      case 'customer':
        return '';
      default:
        return '';
    }
  }
}
